import * as React from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Button,
  makeStyles
} from '@material-ui/core';
import {
  BenefitNetworkChipColor,
  BenefitNetworkKeys,
  BenefitStatusChipColorsObj,
  networkKeyTranslationMapper
} from '@shared/constants/Mapping';
import {useTranslation} from 'react-i18next';
import KeyValueCard from '@components/system/KeyValueCard/KeyValueCard';
import GridTable, {
  DEFAULT_GRID_TABLE__OPTIONS
} from '@components/system/GridTable/GridTable';
import {
  APP,
  BENEFIT_INFORMATION_WRAPPER_BORDER_COLOR,
  CARD_BOX_SHADOW,
  DEFAULT_BLACK_COLOR,
  DEFAULT_GREY_COLOR,
  FONT_PRIMARY_COLOR
} from 'src/style/variable';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import {Benefit} from '../EligibilityVerificationMapper';
import CurrencyFormatter from '@components/system/CurrencyFormatter/CurrencyFormatter';

const styles = makeStyles(() => ({
  title: {
    color: FONT_PRIMARY_COLOR,
    fontWeight: 600,
    marginBottom: '1rem'
  },
  cardStyles: {
    boxShadow: CARD_BOX_SHADOW,
    borderRadius: '1rem',
    padding: '1rem'
  },
  benefitInformationWrapper: {
    backgroundColor: APP.COLOR.DEFAULT.WHITE,
    '&  table': {
      borderLeft: `1px solid ${BENEFIT_INFORMATION_WRAPPER_BORDER_COLOR}`
    },
    '&  tbody': {
      borderLeft: `1px solid ${BENEFIT_INFORMATION_WRAPPER_BORDER_COLOR}`
    }
  },
  chip: {
    backgroundColor: DEFAULT_GREY_COLOR,
    color: 'white',
    padding: '0.2rem 0.5rem',
    borderRadius: '0.2rem',
    lineHeight: '1em'
  },
  benefitInformationTitleWrapper: {
    display: 'flex',
    gap: '1rem',
    flexWrap: 'wrap',
    alignItems: 'center',
    padding: '0.5rem',
    fontWeight: 600,
    color: 'black'
  },
  benefitAccordionSummary: {
    flexDirection: 'row-reverse'
  },
  benefitTitle: {
    fontWeight: 600,
    marginLeft: '1rem'
  },
  benefitDetails: {
    width: '100%'
  },
  benefitChipsWrapper: {
    display: 'flex',
    gap: '0.2rem',
    flexWrap: 'wrap',
    alignItems: 'center',
    marginBottom: '1rem'
  },
  benefitInfoTableWrapper: {
    marginTop: '1rem'
  },
  payerNotesList: {
    listStylePosition: 'inside',
    marginTop: '1rem'
  },
  payerNoteListItem: {
    paddingBlock: '0.5rem'
  },
  amountColumn: {
    lineHeight: '2rem'
  },
  limitationListWrapper: {
    marginBottom: '1rem'
  },
  limitationList: {
    listStylePosition: 'inside',
    display: 'flex',
    flexDirection: 'column',
    gap: '0.3rem'
  },
  amountWrapper: {
    width: 'max-content'
  }
}));

export const itemWithNetworkKeys = [
  {
    key: 'statusDetails',
    getItem: (benefit: any): any => benefit?.['statusDetails']
  },
  {
    key: 'limitations',
    getItem: (benefit: any): any => benefit?.['limitations']
  },
  {
    key: 'coPayment',
    getItem: (benefit: any): any => benefit?.['amounts']?.['coPayment']
  },
  {
    key: 'outOfPocket',
    getItem: (benefit: any): any => benefit?.['amounts']?.['outOfPocket']
  },
  {
    key: 'deductibles',
    getItem: (benefit: any): any => benefit?.['amounts']?.['deductibles']
  },
  {
    key: 'coInsurance',
    getItem: (benefit: any): any => benefit?.['amounts']?.['coInsurance']
  }
];
const addFormattedNetworkBenefitDetails = (benefit: any) => {
  // creating a shallow copy to not modify the benefit obj.
  const formattedBenefit = {...benefit};
  // Initialize all the network as empty object
  BenefitNetworkKeys.forEach(n => (formattedBenefit[n] = {}));

  itemWithNetworkKeys.forEach(item => {
    const itemWithNetworksObj = item.getItem(benefit);
    if (itemWithNetworksObj) {
      for (const key in itemWithNetworksObj) {
        if (BenefitNetworkKeys.includes(key as any)) {
          formattedBenefit[key][item.key] = itemWithNetworksObj[key];
        }
      }
    }
  });

  return formattedBenefit;
};

const getBenefitStatusDetails = (benefit: any) => {
  // Get status details of networks
  const statusDetail = BenefitNetworkKeys.map(nType => {
    if (
      benefit[nType]?.['statusDetails'] &&
      Array.isArray(benefit[nType]?.['statusDetails']) &&
      benefit[nType]?.['statusDetails']?.length > 0
    ) {
      return benefit[nType]?.['statusDetails']?.[0];
    }
    return null;
  }).find(item => !!item);

  const benefitDetails = statusDetail ? {...statusDetail} : {};
  benefitDetails.networks = benefit?.statusDetails
    ? Object.keys(benefit?.statusDetails)
    : [];
  return benefitDetails;
};

const createRowsFromBenefit = (benefit: any) => {
  const rowMap = new Map<string, Record<string, any>>();

  itemWithNetworkKeys.forEach(item => {
    const itemWithNetworksObj = item.getItem(benefit);
    if (itemWithNetworksObj) {
      for (const key in itemWithNetworksObj) {
        if (BenefitNetworkKeys.includes(key as any)) {
          const benefitNetworkItems = itemWithNetworksObj[key];
          if (Array.isArray(benefitNetworkItems)) {
            benefitNetworkItems.map(bItem => {
              if (bItem?.level) {
                const rowMapKey = `${key}+${bItem.level}`;
                if (rowMap.has(rowMapKey)) {
                  const rowMapItem = rowMap.get(rowMapKey) as any;
                  if (rowMapItem[item.key]) {
                    rowMapItem[item.key] = [
                      ...rowMapItem[item.key],
                      {...bItem}
                    ];
                  } else {
                    rowMapItem[item.key] = [{...bItem}];
                  }
                } else {
                  rowMap.set(rowMapKey, {
                    providerType: key,
                    benefitLevel: bItem?.level,
                    [item.key]: [
                      {
                        ...bItem
                      }
                    ]
                  });
                }
              }
            });
          }
        }
      }
    }
  });

  return Array.from(rowMap.values());
};

const BenefitDetail = ({benefit}: {benefit: any}) => {
  const classes = styles();
  const formattedBenefit = addFormattedNetworkBenefitDetails(benefit);

  const rows = createRowsFromBenefit(benefit);

  const formatAmount = (i: any) => {
    const formattedAmount = [];
    if (i?.amount) {
      if (i?.amountTimePeriod) {
        formattedAmount.push(
          <span key="amount-period">{i.amountTimePeriod}: </span>
        );
      }

      if (i?.units === 'USD') {
        formattedAmount.push(
          <CurrencyFormatter
            key="amount"
            value={i.amount || '0'}
            displayType={'text'}
            thousandSeparator={true}
            prefix={'$'}
            ignoreRound={true}
            useSign={true}
          />
        );
      } else if (i?.units === 'Percent') {
        formattedAmount.push(<span key="amount">{i.amount * 100}%</span>);
      } else {
        formattedAmount.push(
          <span key="amount">
            {i?.amount} ${i?.units}
          </span>
        );
      }
    }
    return formattedAmount;
  };

  const {t} = useTranslation();
  const benefitStatusDetails = getBenefitStatusDetails(formattedBenefit);
  const benefitStatusDetailsData = [
    {
      label: t(`ELIGIBILITY_VERIFICATION.COVERAGE_LEVEL`),
      value: benefitStatusDetails?.level || '-'
    },
    {
      label: t('ELIGIBILITY_VERIFICATION.BENEFIT_START_DATE'),
      value: benefitStatusDetails?.benefitBeginDate || '-'
    },
    {
      label: t('ELIGIBILITY_VERIFICATION.BENEFIT_END_DATE'),
      value: benefitStatusDetails?.benefitEndDate || '-'
    }
  ];

  // const getNetworkItemsOfBenefit = (items: any[]) => {
  //   return Array.from(new Set(items.map(item => item.providerType)));
  // };

  // getting authRequired from deductible array
  const getAuthRequired = (row: any) => {
    const benefitItems: any[] = [];
    const fieldsToCheckForAuth = [
      'limitations',
      'deductibles',
      'outOfPocket',
      'coPayment',
      'coInsurance'
    ];
    fieldsToCheckForAuth.map(item => {
      if (Array.isArray(row[item]) && row[item].length > 0) {
        benefitItems.push(...row[item]);
      }
    });
    let isAuthRequired = null;
    for (const bItem of benefitItems) {
      if (bItem?.authRequired === 'Yes') {
        isAuthRequired = true;
        break;
      } else if (bItem?.authRequired === 'No') {
        isAuthRequired = false;
      }
    }
    return isAuthRequired;
  };
  return (
    <div>
      <div className={classes.benefitChipsWrapper}>
        {benefit?.status && (
          <p
            className={classes.chip}
            style={{
              backgroundColor:
                BenefitStatusChipColorsObj?.[benefit?.status]
                  ?.backgroundColor ?? 'grey',
              color:
                BenefitStatusChipColorsObj?.[benefit?.status]?.color ??
                APP.COLOR.DEFAULT.WHITE
            }}>
            {t(
              BenefitStatusChipColorsObj?.[benefit?.status]?.name ||
                benefit?.status
            )}
          </p>
        )}
        {/* {Array.isArray(rows) &&
          rows.length > 0 &&
          getNetworkItemsOfBenefit(rows).map((item: any) => (
            <p
              key={item}
              className={classes.chip}
              style={{
                backgroundColor:
                  (BenefitNetworkChipColor as any)[item]?.backgroundColor ||
                  'grey',
                color:
                  (BenefitNetworkChipColor as any)[item]?.color ||
                  APP.COLOR.DEFAULT.WHITE
              }}>
              {t(
                `ELIGIBILITY_VERIFICATION.${
                  (networkKeyTranslationMapper as any)[item]
                }`
              )}
            </p>
          ))} */}
      </div>
      <KeyValueCard data={benefitStatusDetailsData} />
      {benefit?.payerNotes && Array.isArray(benefit?.payerNotes) && (
        <ul>
          {benefit?.payerNotes.map((note: any, idx: number) => (
            <li key={idx}>{note?.message}</li>
          ))}
        </ul>
      )}

      <div className={classes.benefitInfoTableWrapper}>
        <GridTable
          columns={[
            {
              title: t(
                `ELIGIBILITY_VERIFICATION.BENEFIT_INFORMATION_TABLE.BENEFIT_INFORMATION`
              ),
              render: row => {
                const benefitData = [
                  {
                    label: t(`ELIGIBILITY_VERIFICATION.PLACE_OF_SERVICES`),
                    value: row?.placeOfService || '-'
                  },
                  {
                    label: t(`ELIGIBILITY_VERIFICATION.COVERAGE_LEVEL`),
                    value: row?.level || '-'
                  },
                  {
                    label: t(`ELIGIBILITY_VERIFICATION.COVERAGE_TYPE`),
                    value: row?.benefitLevel || '-'
                  },
                  {
                    label: t('ELIGIBILITY_VERIFICATION.BENEFIT_START_DATE'),
                    value: row?.benefitBeginDate || '-'
                  },
                  {
                    label: t('ELIGIBILITY_VERIFICATION.BENEFIT_END_DATE'),
                    value: row?.benefitEndDate || '-'
                  }
                ];
                return (
                  <div>
                    <div className={classes.benefitChipsWrapper}>
                      <p
                        className={classes.chip}
                        style={{
                          backgroundColor:
                            (BenefitNetworkChipColor as any)[row?.providerType]
                              ?.backgroundColor || 'grey',
                          color:
                            (BenefitNetworkChipColor as any)[row?.providerType]
                              ?.color || APP.COLOR.DEFAULT.WHITE
                        }}>
                        {t(
                          `ELIGIBILITY_VERIFICATION.${
                            (networkKeyTranslationMapper as any)[
                              row?.providerType
                            ]
                          }`
                        ) ?? row?.providerType}
                      </p>
                    </div>
                    <KeyValueCard data={benefitData} />
                    {row?.payerNotes &&
                      Array.isArray(row?.payerNotes) &&
                      row?.payerNotes?.length > 0 && (
                        <ul className={classes.payerNotesList}>
                          {row?.payerNotes.map((note: any, idx: number) => (
                            <li key={idx} className={classes.payerNoteListItem}>
                              {note}
                            </li>
                          ))}
                        </ul>
                      )}
                  </div>
                );
              }
            },
            {
              title: t(
                `ELIGIBILITY_VERIFICATION.BENEFIT_INFORMATION_TABLE.CO_INSURANCE`
              ),
              render: row => {
                if (
                  row?.['coInsurance'] &&
                  Array.isArray(row?.['coInsurance']) &&
                  row?.['coInsurance']?.length > 0
                ) {
                  return (
                    <p className={classes.amountColumn}>
                      {Array.isArray(row?.['coInsurance']) &&
                        row['coInsurance']
                          .filter(item => item?.amount)
                          .map((item, idx) => (
                            <div key={idx}>
                              <p className={classes.amountWrapper}>
                                {formatAmount(item)}
                              </p>
                              {item?.additionalInfo && (
                                <p>Additional Info: {item.additionalInfo}</p>
                              )}
                            </div>
                          ))}
                    </p>
                  );
                }
                return '-';
              }
            },
            {
              title: t(
                `ELIGIBILITY_VERIFICATION.BENEFIT_INFORMATION_TABLE.CO_PAYMENT`
              ),
              render: row => {
                if (
                  row?.['coPayment'] &&
                  Array.isArray(row?.['coPayment']) &&
                  row?.['coPayment'].length > 0
                ) {
                  return (
                    <p className={classes.amountColumn}>
                      {Array.isArray(row?.['coPayment']) &&
                        row['coPayment']
                          .filter(item => item?.amount)
                          .map((item, idx) => (
                            <p className={classes.amountWrapper} key={idx}>
                              {formatAmount(item)}
                            </p>
                          ))}
                    </p>
                  );
                }
                return '-';
              }
            },
            {
              title: t(
                `ELIGIBILITY_VERIFICATION.BENEFIT_INFORMATION_TABLE.OUT_OF_POCKET`
              ),
              render: row => {
                if (
                  row?.['outOfPocket'] &&
                  Array.isArray(row?.['outOfPocket']) &&
                  row?.['outOfPocket'].length > 0
                ) {
                  return (
                    <p className={classes.amountColumn}>
                      {Array.isArray(row?.['outOfPocket']) &&
                        row['outOfPocket']
                          .filter(item => item?.amount)
                          .map((item, idx) => (
                            <p className={classes.amountWrapper} key={idx}>
                              {formatAmount(item)}
                            </p>
                          ))}
                    </p>
                  );
                }
                return '-';
              }
            },
            {
              title: t(
                `ELIGIBILITY_VERIFICATION.BENEFIT_INFORMATION_TABLE.BENEFIT_DEDUCTIBLE`
              ),
              render: row => {
                if (
                  row?.['deductibles'] &&
                  Array.isArray(row?.['deductibles']) &&
                  row?.['deductibles']?.length > 0
                ) {
                  return (
                    <p className={classes.amountColumn}>
                      {Array.isArray(row?.['deductibles']) &&
                        row['deductibles']
                          .filter(item => item?.amount)
                          .map((item, idx) => (
                            <p className={classes.amountWrapper} key={idx}>
                              {formatAmount(item)}
                            </p>
                          ))}
                    </p>
                  );
                }
                return '-';
              }
            },
            {
              title: t(
                `ELIGIBILITY_VERIFICATION.BENEFIT_INFORMATION_TABLE.LIMITATIONS`
              ),
              render: row => {
                if (
                  row?.['limitations'] &&
                  Array.isArray(row?.['limitations']) &&
                  row?.['limitations']?.length > 0
                ) {
                  return (
                    <div>
                      {row?.['limitations']?.map((item, idx) => {
                        if (item?.quantity) {
                          return (
                            <div
                              key={idx}
                              className={classes.limitationListWrapper}>
                              <p>
                                {item?.quantity} {item?.quantityQualifier ?? ''}
                              </p>
                              <ul className={classes.limitationList}>
                                {item?.payerNotes &&
                                  Array.isArray(item?.payerNotes) &&
                                  item?.payerNotes?.map((note: string) => (
                                    <li key={note}>{note}</li>
                                  ))}
                              </ul>
                            </div>
                          );
                        }
                      })}
                    </div>
                  );
                }
                return '-';
              }
            },
            {
              title: t(
                `ELIGIBILITY_VERIFICATION.BENEFIT_INFORMATION_TABLE.AUTHORIZATION`
              ),
              render: row => {
                const isAuthRequired = getAuthRequired(row);

                return isAuthRequired === true
                  ? t(
                      `ELIGIBILITY_VERIFICATION.BENEFIT_INFORMATION_TABLE.AUTHORIZATION_REQUIRED`
                    )
                  : isAuthRequired === false
                  ? t(
                      `ELIGIBILITY_VERIFICATION.BENEFIT_INFORMATION_TABLE.AUTHORIZATION_NOT_REQUIRED`
                    )
                  : '-';
              }
            }
          ]}
          data={rows}
          options={{
            ...DEFAULT_GRID_TABLE__OPTIONS,
            emptyRowsWhenPaging: false,
            sorting: true,
            paging: false,
            showEmptyDataSourceMessage: true,
            maxBodyHeight: '100%',
            actionsColumnIndex: -1,
            tableLayout: 'auto',
            detailPanelColumnAlignment: 'left',
            // selection: request,
            draggable: false,
            // showSelectAllCheckbox: request,
            headerStyle: {
              color: DEFAULT_BLACK_COLOR,
              fontFamily: APP.FONT_FAMILY.MEDIUM,
              flexDirection: 'row',
              zIndex: 100
            }
          }}
          style={{
            // boxShadow: tableBoxShadow,
            margin: '0rem',
            marginBottom: '2rem',
            marginTop: '0rem',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'auto',
            boxShadow:
              '0 0 0 1px rgb(63 63 68 / 5%), 0 1px 3px 0 rgb(63 63 68 / 15%)'
          }}
        />
      </div>
    </div>
  );
};

const BenefitAccordion = ({
  benefit,
  expanded = false,
  defaultExpanded = false
}: {
  benefit: any;
  expanded?: boolean;
  defaultExpanded?: boolean;
}) => {
  const [isBenefitsExpanded, setIsBenefitExpanded] = React.useState(expanded);

  // It is to expand the benefit, if expand or collapse all benefit button is pressed
  React.useEffect(() => {
    setIsBenefitExpanded(expanded);
  }, [expanded]);

  React.useEffect(() => {
    if (defaultExpanded) {
      setIsBenefitExpanded(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const classes = styles();
  return (
    <Accordion expanded={isBenefitsExpanded}>
      <AccordionSummary
        onClick={() => setIsBenefitExpanded(p => !p)}
        expandIcon={<ExpandMoreIcon />}
        className={classes.benefitAccordionSummary}>
        <p className={classes.benefitTitle}>
          {`${benefit?.name ?? ''} ${
            benefit?.type ? `- ${benefit?.type}` : ''
          }` || '-'}
        </p>
      </AccordionSummary>
      <AccordionDetails>
        <div className={classes.benefitDetails}>
          <BenefitDetail benefit={benefit} />
        </div>
      </AccordionDetails>
    </Accordion>
  );
};

const BenefitInformation = ({
  data,
  expandFirstBenefit = false
}: {
  data: Benefit[];
  expandFirstBenefit: boolean;
}): JSX.Element => {
  const classes = styles();
  const {t} = useTranslation();
  const [isBenefitsExpanded, setIsBenefitExpanded] = React.useState(false);
  return (
    <div className={classes.benefitInformationWrapper}>
      <div className={classes.benefitInformationTitleWrapper}>
        <p>{t('ELIGIBILITY_VERIFICATION.BENEFIT_INFORMATION')}</p>
        {data && Array.isArray(data) && (
          <Button
            color="primary"
            variant="outlined"
            className={`toggle-button`}
            onClick={() => setIsBenefitExpanded(p => !p)}>
            {isBenefitsExpanded
              ? t('ELIGIBILITY_VERIFICATION.COLLAPSE_ALL')
              : t('ELIGIBILITY_VERIFICATION.EXPAND_ALL')}
          </Button>
        )}
      </div>
      {data &&
        Array.isArray(data) &&
        data.map((benefit, idx) => {
          return (
            <BenefitAccordion
              key={idx}
              expanded={isBenefitsExpanded}
              benefit={benefit}
              defaultExpanded={idx === 0 && expandFirstBenefit}
            />
          );
        })}
    </div>
  );
};

export default BenefitInformation;